/**
 * Fuzzy text matching utilities for PDF highlighting
 * Uses Jaro-Winkler similarity for fast, robust matching
 *
 * Performance: 50-200x faster than previous LCS-based approach
 */

// =============================================================================
// Search Algorithm Tuning Constants
// =============================================================================

/**
 * Multiplier for trigram pre-filter threshold (applied to main threshold).
 * Lower values allow more candidates through for detailed matching.
 * 0.5 means trigram similarity must be at least half the main threshold.
 */
const TRIGRAM_THRESHOLD_FACTOR = 0.5;

/**
 * Similarity threshold for early termination.
 * When a match reaches this similarity, stop searching immediately.
 * 0.95 balances finding near-perfect matches quickly vs thoroughness.
 */
const EARLY_TERMINATION_THRESHOLD = 0.95;

/**
 * Divisor for calculating coarse search step size.
 * Step = needleLength / COARSE_SEARCH_STEP_DIVISOR.
 * Higher values = finer initial search but slower.
 * 4 means checking every ~25% of needle length positions initially.
 */
const COARSE_SEARCH_STEP_DIVISOR = 4;

/**
 * Multiplier for fine search activation threshold (applied to main threshold).
 * Fine search runs when coarse search finds a match above this threshold.
 * 0.8 means refine search if we found something at least 80% of threshold.
 */
const FINE_SEARCH_THRESHOLD_FACTOR = 0.8;

// =============================================================================
// Text Normalization
// =============================================================================

// Common ligatures and their expanded forms
const LIGATURE_MAP: Record<string, string> = {
  '\ufb00': 'ff',   // ﬀ
  '\ufb01': 'fi',   // ﬁ
  '\ufb02': 'fl',   // ﬂ
  '\ufb03': 'ffi',  // ﬃ
  '\ufb04': 'ffl',  // ﬄ
  '\ufb05': 'st',   // ﬅ (long s + t)
  '\ufb06': 'st',   // ﬆ
  '\u0152': 'OE',   // Œ
  '\u0153': 'oe',   // œ
  '\u00c6': 'AE',   // Æ
  '\u00e6': 'ae',   // æ
};

// OCR-confusable character mappings for regex patterns
export const OCR_CHAR_CLASSES: Record<string, string> = {
  '0': '[0O]',
  'O': '[0O]',
  'o': '[0oO]',
  '1': '[1lI|]',
  'l': '[1lI|]',
  'I': '[1lI|]',
  '|': '[1lI|]',
  '5': '[5S]',
  'S': '[5S]',
  's': '[5s]',
  '8': '[8B]',
  'B': '[8B]',
  '2': '[2Z]',
  'Z': '[2Z]',
  'z': '[2z]',
  '6': '[6G]',
  'G': '[6G]',
  '9': '[9g]',
  'g': '[9g]',
  'rn': '(rn|m)',
  'm': '(m|rn)',
};

export interface FuzzyMatchResult {
  found: boolean;
  matchedText: string;
  startIndex: number;
  endIndex: number;
  similarity: number; // 0-1, where 1 is exact match
  matchType: 'exact' | 'regex' | 'fuzzy';
}

/**
 * Normalize text for matching:
 * - Apply Unicode NFC normalization
 * - Expand ligatures to character sequences
 * - Normalize whitespace
 * - Convert to lowercase for comparison
 */
export function normalizeForMatching(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  let normalized = text;

  // Unicode NFC normalization
  if (normalized.normalize) {
    normalized = normalized.normalize('NFC');
  }

  // Expand ligatures
  for (const [ligature, expansion] of Object.entries(LIGATURE_MAP)) {
    normalized = normalized.split(ligature).join(expansion);
  }

  // Normalize non-breaking spaces to regular spaces
  normalized = normalized.replace(/\u00a0/g, ' ');

  // Collapse multiple whitespace to single space
  normalized = normalized.replace(/\s+/g, ' ');

  // Trim
  normalized = normalized.trim();

  return normalized;
}

/**
 * Generate character trigrams from a string
 * Used for fast initial similarity filtering
 */
function generateTrigrams(text: string): Set<string> {
  const trigrams = new Set<string>();
  const normalized = text.toLowerCase();

  for (let i = 0; i <= normalized.length - 3; i++) {
    trigrams.add(normalized.substring(i, i + 3));
  }

  return trigrams;
}

/**
 * Calculate trigram similarity (Jaccard index)
 * Fast O(n) check before expensive LCS
 * Returns value between 0 and 1
 */
export function trigramSimilarity(a: string, b: string): number {
  if (!a || !b) return 0;
  if (a === b) return 1;

  const trigramsA = generateTrigrams(a);
  const trigramsB = generateTrigrams(b);

  if (trigramsA.size === 0 || trigramsB.size === 0) {
    return 0;
  }

  let intersection = 0;
  for (const trigram of trigramsA) {
    if (trigramsB.has(trigram)) {
      intersection++;
    }
  }

  const union = trigramsA.size + trigramsB.size - intersection;
  return union > 0 ? intersection / union : 0;
}

/**
 * Calculate Longest Common Subsequence length
 * Space-optimized DP: O(n) space instead of O(n*m)
 */
function longestCommonSubsequence(a: string, b: string): number {
  if (!a || !b) return 0;

  const m = a.length;
  const n = b.length;

  // Use shorter string as columns for space optimization
  if (m < n) {
    return longestCommonSubsequence(b, a);
  }

  let prev = new Array(n + 1).fill(0);
  let curr = new Array(n + 1).fill(0);

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        curr[j] = prev[j - 1] + 1;
      } else {
        curr[j] = Math.max(prev[j], curr[j - 1]);
      }
    }
    [prev, curr] = [curr, prev];
  }

  return prev[n];
}

/**
 * Calculate LCS-based similarity score
 * Returns value between 0 and 1
 * NOTE: This is expensive O(m*n) - prefer jaroWinklerSimilarity for performance
 */
export function lcsSimilarity(a: string, b: string): number {
  if (!a || !b) return 0;
  if (a === b) return 1;

  const lcsLen = longestCommonSubsequence(a, b);
  // Dice coefficient: 2 * |LCS| / (|a| + |b|)
  return (2 * lcsLen) / (a.length + b.length);
}

/**
 * Calculate Jaro similarity between two strings
 * Time complexity: O(m + n) - much faster than LCS
 * Returns value between 0 and 1
 */
function jaroSimilarity(s1: string, s2: string): number {
  if (!s1 || !s2) return 0;
  if (s1 === s2) return 1;

  const len1 = s1.length;
  const len2 = s2.length;

  // Maximum distance for matching characters
  const matchDistance = Math.floor(Math.max(len1, len2) / 2) - 1;
  if (matchDistance < 0) return 0;

  const s1Matches = new Array(len1).fill(false);
  const s2Matches = new Array(len2).fill(false);

  let matches = 0;
  let transpositions = 0;

  // Find matching characters
  for (let i = 0; i < len1; i++) {
    const start = Math.max(0, i - matchDistance);
    const end = Math.min(i + matchDistance + 1, len2);

    for (let j = start; j < end; j++) {
      if (s2Matches[j] || s1[i] !== s2[j]) continue;
      s1Matches[i] = true;
      s2Matches[j] = true;
      matches++;
      break;
    }
  }

  if (matches === 0) return 0;

  // Count transpositions
  let k = 0;
  for (let i = 0; i < len1; i++) {
    if (!s1Matches[i]) continue;
    while (!s2Matches[k]) k++;
    if (s1[i] !== s2[k]) transpositions++;
    k++;
  }

  return (
    (matches / len1 + matches / len2 + (matches - transpositions / 2) / matches) / 3
  );
}

/**
 * Calculate Jaro-Winkler similarity between two strings
 * Time complexity: O(m + n) - 80-150x faster than LCS for typical inputs
 * Gives bonus weight to common prefix, good for OCR errors and typos
 * Returns value between 0 and 1
 */
export function jaroWinklerSimilarity(s1: string, s2: string, prefixScale: number = 0.1): number {
  if (!s1 || !s2) return 0;
  if (s1 === s2) return 1;

  const jaroScore = jaroSimilarity(s1, s2);

  // Find common prefix (up to 4 characters)
  let prefixLength = 0;
  const maxPrefix = Math.min(4, Math.min(s1.length, s2.length));
  for (let i = 0; i < maxPrefix; i++) {
    if (s1[i] === s2[i]) {
      prefixLength++;
    } else {
      break;
    }
  }

  return jaroScore + prefixLength * prefixScale * (1 - jaroScore);
}

/**
 * Generate trigrams from pre-normalized text (no lowercasing).
 * Used internally where text is already normalized/lowercased.
 * Note: Separate from generateTrigrams() which handles raw input with lowercasing.
 */
function generateTrigramsFromNormalized(text: string): Set<string> {
  const trigrams = new Set<string>();
  for (let i = 0; i <= text.length - 3; i++) {
    trigrams.add(text.substring(i, i + 3));
  }
  return trigrams;
}

/**
 * Calculate Jaccard similarity between two pre-computed trigram sets.
 * Returns a value between 0 (no overlap) and 1 (identical sets).
 */
function calculateTrigramJaccardSimilarity(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;

  let intersection = 0;
  for (const trigram of a) {
    if (b.has(trigram)) intersection++;
  }

  const union = a.size + b.size - intersection;
  return union > 0 ? intersection / union : 0;
}

/**
 * Generate sampled window sizes for fuzzy matching
 * Samples 5 sizes across the range instead of all sizes
 */
function getSampledWindowSizes(needleLength: number): number[] {
  const minSize = Math.floor(needleLength * 0.8);
  const maxSize = Math.ceil(needleLength * 1.5);
  const range = maxSize - minSize;

  if (range <= 5) {
    return Array.from({ length: range + 1 }, (_, i) => minSize + i);
  }

  const sizes: number[] = [];
  for (let i = 0; i < 5; i++) {
    const w = Math.round(minSize + (range * i) / 4);
    if (!sizes.includes(w)) sizes.push(w);
  }
  return sizes;
}

/** Context for sliding window search */
interface SearchContext {
  normalizedNeedle: string;
  normalizedHaystack: string;
  originalHaystack: string;
  needleTrigrams: Set<string>;
  trigramThreshold: number;
  earlyTermThreshold: number;
}

/** Mutable search state */
interface SearchState {
  bestMatch: FuzzyMatchResult | null;
  bestSimilarity: number;
}

/**
 * Check a single window position and update state if better match found
 */
function evaluateWindow(
  ctx: SearchContext,
  state: SearchState,
  position: number,
  windowSize: number
): boolean {
  const window = ctx.normalizedHaystack.substring(position, position + windowSize);
  const windowTrigrams = generateTrigramsFromNormalized(window);

  if (calculateTrigramJaccardSimilarity(ctx.needleTrigrams, windowTrigrams) < ctx.trigramThreshold) {
    return false;
  }

  const similarity = jaroWinklerSimilarity(ctx.normalizedNeedle, window);
  if (similarity <= state.bestSimilarity) {
    return false;
  }

  state.bestSimilarity = similarity;
  state.bestMatch = {
    found: true,
    matchedText: ctx.originalHaystack.substring(position, position + windowSize),
    startIndex: position,
    endIndex: position + windowSize,
    similarity,
    matchType: 'fuzzy',
  };

  return similarity >= ctx.earlyTermThreshold;
}

/**
 * Run coarse search pass over haystack
 */
function coarseSearch(
  ctx: SearchContext,
  state: SearchState,
  windowSize: number,
  step: number
): boolean {
  const maxPos = ctx.normalizedHaystack.length - windowSize;
  for (let i = 0; i <= maxPos; i += step) {
    if (evaluateWindow(ctx, state, i, windowSize)) return true;
  }
  return false;
}

/**
 * Run fine-grained search around a region
 */
function fineSearch(
  ctx: SearchContext,
  state: SearchState,
  windowSize: number,
  region: number,
  step: number
): boolean {
  const maxPos = ctx.normalizedHaystack.length - windowSize;
  const start = Math.max(0, region - step);
  const end = Math.min(maxPos, region + step);

  for (let i = start; i <= end; i++) {
    if (i % step === 0) continue;
    if (evaluateWindow(ctx, state, i, windowSize)) return true;
  }
  return false;
}

/**
 * Find the best matching substring in haystack for the needle
 * Uses optimized sliding window with Jaro-Winkler similarity and early termination
 *
 * Performance optimizations:
 * 1. Uses Jaro-Winkler (O(m+n)) instead of LCS (O(m*n)) - 80-150x faster
 * 2. Samples 5 window sizes instead of iterating all - 3x fewer iterations
 * 3. Early termination on high-confidence matches (>=0.95)
 * 4. Coarse-to-fine search to avoid checking every position
 */
export function fuzzyFindSubstring(
  needle: string,
  haystack: string,
  threshold: number = 0.75
): FuzzyMatchResult | null {
  if (!needle || !haystack) return null;

  const normalizedNeedle = normalizeForMatching(needle).toLowerCase();
  const normalizedHaystack = normalizeForMatching(haystack).toLowerCase();

  if (!normalizedNeedle || !normalizedHaystack) return null;
  if (normalizedNeedle.length > normalizedHaystack.length) return null;

  // Fast path: exact substring match
  const exactIndex = normalizedHaystack.indexOf(normalizedNeedle);
  if (exactIndex !== -1) {
    const originalHaystack = normalizeForMatching(haystack);
    return {
      found: true,
      matchedText: originalHaystack.substring(exactIndex, exactIndex + normalizedNeedle.length),
      startIndex: exactIndex,
      endIndex: exactIndex + normalizedNeedle.length,
      similarity: 1,
      matchType: 'exact',
    };
  }

  // Prepare search context
  const ctx: SearchContext = {
    normalizedNeedle,
    normalizedHaystack,
    originalHaystack: normalizeForMatching(haystack),
    needleTrigrams: generateTrigramsFromNormalized(normalizedNeedle),
    trigramThreshold: threshold * TRIGRAM_THRESHOLD_FACTOR,
    earlyTermThreshold: EARLY_TERMINATION_THRESHOLD,
  };

  const state: SearchState = { bestMatch: null, bestSimilarity: 0 };
  const windowSizes = getSampledWindowSizes(normalizedNeedle.length);
  const step = Math.max(1, Math.floor(normalizedNeedle.length / COARSE_SEARCH_STEP_DIVISOR));
  const fineThreshold = threshold * FINE_SEARCH_THRESHOLD_FACTOR;

  for (const windowSize of windowSizes) {
    if (coarseSearch(ctx, state, windowSize, step)) return state.bestMatch;

    if (state.bestMatch && state.bestSimilarity >= fineThreshold) {
      if (fineSearch(ctx, state, windowSize, state.bestMatch.startIndex, step)) {
        return state.bestMatch;
      }
    }
  }

  return state.bestSimilarity >= threshold ? state.bestMatch : null;
}

/**
 * Escape special regex characters in a string
 */
export function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Convert a fuzzy match result to a regex pattern for the search plugin
 * This creates an exact match for the text found in the PDF
 */
export function fuzzyMatchToRegex(matchedText: string): RegExp | null {
  if (!matchedText) {
    return null;
  }

  // Escape for regex and allow flexible whitespace
  const tokens = matchedText.match(/[A-Za-z]+|[0-9]+|[^A-Za-z0-9\s]/g) || [];

  if (tokens.length === 0) {
    return null;
  }

  const pattern = tokens.map(token => escapeRegExp(token)).join('\\s*');

  try {
    return new RegExp(pattern, 'gi');
  } catch {
    return null;
  }
}
